/**
 * Audience/geo restriction types for A/B experiments.
 *
 * Include-list semantics: an empty or absent list means "no restriction on that
 * axis" (all values allowed). `enabled: false` disables the whole restriction.
 * The wizard never sends `enabled: true` with every list empty — see
 * `normalizeRestriction` helpers in CreateExperiment.
 *
 * Runtime enforcement lives in the backend tracking-script generators; the
 * contract is documented in docs/TEMP_EXPERIMENT_RESTRICTIONS_RUNTIME_CONTRACT.md.
 */

export type RestrictionDevice = "desktop" | "mobile" | "tablet";

export type RestrictionChannel = "organic" | "paid" | "direct" | "referral";

export const RESTRICTION_DEVICES: RestrictionDevice[] = [
  "desktop",
  "mobile",
  "tablet",
];

export const RESTRICTION_CHANNELS: RestrictionChannel[] = [
  "organic",
  "paid",
  "direct",
  "referral",
];

export type GeoRestriction = {
  enabled: boolean;
  /** ISO 3166-1 alpha-2 country codes (uppercased). */
  countries?: string[];
};

export type AudienceRestriction = {
  enabled: boolean;
  devices?: RestrictionDevice[];
  channels?: RestrictionChannel[];
};

/**
 * Returns the geo restriction to send on create, or `undefined` when it should
 * not be applied (disabled, or enabled with no countries selected).
 */
export function normalizeGeoRestriction(
  geo: GeoRestriction | undefined
): GeoRestriction | undefined {
  const countries = geo?.countries?.filter(Boolean) ?? [];
  if (!countries.length) return undefined;
  return { enabled: true, countries };
}

/**
 * Returns the audience restriction to send on create, or `undefined` when it
 * should not be applied (disabled, or enabled with no devices/channels selected).
 */
export function normalizeAudienceRestriction(
  audience: AudienceRestriction | undefined
): AudienceRestriction | undefined {
  const devices = audience?.devices?.filter(Boolean) ?? [];
  const channels = audience?.channels?.filter(Boolean) ?? [];
  if (!devices.length && !channels.length) return undefined;
  return {
    enabled: true,
    ...(devices.length ? { devices } : {}),
    ...(channels.length ? { channels } : {}),
  };
}
